'use client';

import { Button } from '@ahafood/ui/components/button';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import InlineLoadingSkeletons from './inline-loading-skeletons';
import SoloMealItemCard from './solo-meal-item-card';
import {
  getSoloMealItemFn,
  type SoloMealItem,
} from '@/api/use-get-solomeal-item';
import { LoginDialog } from '@/app/[locale]/(main)/store/components/fb-login';
import { useCityRoutes } from '@/hooks/use-city-navigation';
import { useImpressionTrackingReset } from '@/hooks/use-item-impression';
import { Link } from '@/i18n/routing';
import { trackEvent } from '@/lib/event-tracking';
import { useAuth } from '@/providers/auth-provider';
import { useAuthStore } from '@/store';

const LIMIT = 50;

export default function SoloMealItemList({
  lat,
  lng,
  initialData = [],
  category,
}: {
  lat: number;
  lng: number;
  initialData?: SoloMealItem[];
  category?: string;
}) {
  const routes = useCityRoutes();
  const { isAuthenticated } = useAuthStore();
  const { user } = useAuth();
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const queryClient = useQueryClient();
  const token = user?.systemToken;

  // Auto-reset impression tracking khi navigate hoặc chuyển tab
  useImpressionTrackingReset();

  useEffect(() => {
    if (isAuthenticated) setShowLoginDialog(false);
  }, [isAuthenticated]);

  // Refetch when lat/lng or token changes
  useEffect(() => {
    void queryClient.invalidateQueries({
      queryKey: ['solo-meal-items', { lat, lng, category, token }],
    });
  }, [lat, lng, category, token, queryClient]);

  const { data, fetchNextPage, hasNextPage, isLoading } = useInfiniteQuery({
    queryKey: ['solo-meal-items', { lat, lng, category, token }],
    queryFn: ({ pageParam = 0 }) =>
      getSoloMealItemFn(
        { lat, lng, limit: LIMIT, skip: pageParam, category },
        {},
        token
      ),
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce(
        (acc, page) => acc + (page?.solo_meal_items?.length || 0),
        0
      );
      return lastPage.solo_meal_items.length < LIMIT ? undefined : loaded;
    },
    initialPageParam: 0,
    initialData: initialData.length
      ? { pages: [{ solo_meal_items: initialData }], pageParams: [0] }
      : undefined,
    // Enhanced caching for smooth navigation
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnMount: false, // Don't refetch if we have fresh data
    refetchOnWindowFocus: false,
    // Disable query when lat/lng are missing or equal to 0
    enabled: !!(lat && lng && lat !== 0 && lng !== 0),
  });

  const itemClickTrack = (item: SoloMealItem, index: number) => {
    trackEvent('click_button', {
      action_time: Math.floor(Date.now() / 1000),
      button_name: 'click_solomeal_card',
      button_location: 'solomeal_list',
      store_id: item.store_id?.toString() || '',
      user_id: user?.id ?? '',
      item_id: item._id,
      item_fee: item.discounted_price,
      item_name: item.name,
      item_discount: item.price - item.discounted_price,
      distance: item.distance,
      ratting: item.rating.average,
      item_index: index,
    });
  };

  const soloMealItems = useMemo(
    () =>
      data?.pages?.flatMap((page) => page.solo_meal_items).filter(Boolean) ??
      [],
    [data]
  );

  const renderHeader = (
    <div className="mb-4 pt-4">
      <h2
        className="text-lg font-bold text-neutral-90 mb-1"
        data-testid="solomeal-list-title"
      >
        Danh sách Solo Meal gần bạn
      </h2>
    </div>
  );

  if (isLoading && !soloMealItems.length) {
    return (
      <div className="px-4 pb-20 w-full max-w-screen-xl mx-auto">
        {renderHeader}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
          <InlineLoadingSkeletons />
        </div>
      </div>
    );
  }

  return (
    <div
      className="px-4 pb-20 w-full max-w-screen-xl mx-auto"
      data-testid="solomeal-list-section"
    >
      {renderHeader}
      <InfiniteScroll
        className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4"
        dataLength={soloMealItems.length}
        next={fetchNextPage}
        hasMore={!!hasNextPage}
        loader={<InlineLoadingSkeletons />}
        endMessage={
          <div className="col-span-full text-center py-8">
            <Link href={routes.home}>
              <Button
                // onClick={() => router.push(routes.home)}
                variant="outline"
                className="border-primary w-full font-bold text-primary"
                data-testid="solomeal-more-store-button"
              >
                Khám phá thêm Quán ăn
              </Button>
            </Link>
          </div>
        }
      >
        {soloMealItems.length ? (
          soloMealItems.map((item, index) => (
            <SoloMealItemCard
              key={item._id}
              item={item}
              index={index}
              isAuthenticated={isAuthenticated}
              setShowLoginDialog={setShowLoginDialog}
              onClick={() => itemClickTrack(item, index)}
              onAddToCartClick={() =>
                trackEvent('click_button', {
                  action_time: Math.floor(Date.now() / 1000),
                  button_name: 'click_solomeal_add_to_cart',
                  button_location: 'solomeal_list',
                  store_id: item.store_id?.toString() || '',
                  user_id: user?.id ?? '',
                  item_id: item._id,
                  item_fee: item.discounted_price,
                  item_name: item.name,
                  item_discount: item.price - item.discounted_price,
                  distance: item.distance,
                  ratting: item.rating.average,
                  item_index: index,
                })
              }
            />
          ))
        ) : (
          <div className="col-span-full text-center py-8 text-neutral-60">
            Không có món ăn nào.
          </div>
        )}
      </InfiniteScroll>

      <LoginDialog
        open={showLoginDialog}
        onOpenChange={setShowLoginDialog}
        location="solo_meal_list"
      />
    </div>
  );
}
